import React from 'react';
import Button from '@mui/material/Button';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NoNewNotificationsComponent from './NoNewNotificationsComponent';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/system';
import { getIconForType } from '../NRViewActivityFeed/NRActivityFeedContent';
import EllipsisTypography from '../EllipsisTypography/EllipsisTypography';
import NRRelativeTime from '../NRRelativeTime/NRRelativeTime';
import CircleIcon from '@mui/icons-material/Circle';
import { Link } from 'react-router-dom';
import { CONSTANTS } from '../../constants/constants';
import { encodeParam } from '../../services/params.utils';

const { BINARIES, CREDENTIALS, CRYPTOGRAPHY, SBOM, VULNERABILITIES } = CONSTANTS.REPORT.TABS;
const { EQUAL } = CONSTANTS.FILTER_OPERATIONS;
const { DETECTED_HASHES, USER_ACCOUNTS } = CONSTANTS.REPORT.CREDENTIALS_SUB_TABS;
const { CERTIFICATES, PRIVATE_KEYS } = CONSTANTS.REPORT.CRYPTOGRAPHY_SUB_TABS;

const AltAvatar = styled(props => <Avatar {...props} />)(({ theme }) => ({
  backgroundColor: theme.palette.text.avatarAlt.background,
  color: theme.palette.text.iconInfo,
  width: theme.spacing(4),
  height: theme.spacing(4)
}));

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  '& .notification-title': {
    color: theme.palette.text.primary
  },
  '&:visited .notification-title': {
    color: theme.palette.text.secondary
  },
  '& .notification-dot': {
    color: theme.palette.info.main
  },
  '&:visited .notification-dot': {
    color: theme.palette.text.iconInfo
  }
}));

// Build notification URL following the pattern from NRNewSearchResultCard
function buildNotificationUrl(notification) {
  const { parser, assetId, entry } = notification;
  const redirectToReportURL = '/assets/' + assetId;

  let extraParams = new URLSearchParams();
  let tab = '';
  let subTab;

  switch (parser) {
    case 'COMPONENT':
      tab = SBOM;
      extraParams.append(
        'componentColumnFilter',
        encodeParam([
          {
            fieldName: 'NAME',
            value: entry?.componentName,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          }
        ])
      );
      break;
    case 'HASH':
      tab = CREDENTIALS;
      subTab = encodeParam(DETECTED_HASHES);
      extraParams.append(
        'hashesColumnFilter',
        encodeParam([
          {
            fieldName: 'FILEPATH',
            value: entry?.filePath,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          },
          {
            fieldName: 'HASH',
            value: entry?.hash,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          }
        ])
      );
      break;
    case 'LINUXAUTH':
      tab = CREDENTIALS;
      subTab = encodeParam(USER_ACCOUNTS);
      extraParams.append(
        'userAccountsColumnFilter',
        encodeParam([
          {
            fieldName: 'FILEPATH',
            value: entry?.filePath,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          },
          {
            fieldName: 'USERNAME',
            value: entry?.username,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          }
        ])
      );
      break;
    case 'CVE':
      tab = VULNERABILITIES;
      extraParams.append(
        'vulnerabilityColumnFilter',
        encodeParam([
          {
            fieldName: 'CVE',
            value: entry?.cveId,
            operation: EQUAL
          }
        ])
      );
      break;
    case 'BINARY_PROTECTIONS':
      tab = BINARIES;
      extraParams.append(
        'binaryProtectionColumnFilter',
        encodeParam([
          {
            fieldName: 'FILEPATH',
            value: entry?.filePath,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          }
        ])
      );
      break;
    case 'CERTIFICATE':
      tab = CRYPTOGRAPHY;
      subTab = encodeParam(CERTIFICATES);
      extraParams.append(
        'certificateColumnFilter',
        encodeParam([
          {
            fieldName: 'FILEPATH',
            value: entry?.filePath,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          },
          {
            fieldName: 'SHA256',
            value: entry?.sha256Thumbprint,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          }
        ])
      );
      break;
    case 'PRIVATEKEY':
      tab = CRYPTOGRAPHY;
      subTab = encodeParam(PRIVATE_KEYS);
      extraParams.append(
        'privateKeyColumnFilter',
        encodeParam([
          {
            fieldName: 'FILEPATH',
            value: entry?.filePath,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          },
          {
            fieldName: 'MATCHHASH',
            value: entry?.matchHash,
            operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
          }
        ])
      );
      break;
    case 'ASSET':
    default:
      // Default to asset page
      return redirectToReportURL;
  }

  return `${redirectToReportURL}${!!tab ? '/' + tab : ''}${!!subTab ? '/' + subTab : ''}?${extraParams.toString()}`;
}

function NotificationItem({ item, onNotificationClick }) {
  const theme = useTheme();
  const { description, title, type, id, createdAt, assetName } = item;
  const createdAtAsDate = new Date(createdAt);

  const notificationUrl = buildNotificationUrl(item);

  const handleClick = () => {
    if (onNotificationClick) {
      onNotificationClick(id);
    }
  };

  return (
    <StyledLink to={notificationUrl} onClick={handleClick}>
      <Box
        key={id}
        gap={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          height: theme.spacing(9),
          position: 'relative',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        <AltAvatar>{getIconForType(type)}</AltAvatar>
        <Box
          sx={{
            marginLeft: 2,
            overflow: 'hidden',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0.5
          }}
        >
          <EllipsisTypography variant="body2" className="notification-title">
            {title}
          </EllipsisTypography>
          <EllipsisTypography variant="caption" color="text.secondary">
            {description}
          </EllipsisTypography>
          <EllipsisTypography variant="caption" color="text.secondary">
            {assetName}
          </EllipsisTypography>
        </Box>
        <Box gap={1} display="flex" alignItems="center" sx={{ marginLeft: 'auto', flexShrink: 0 }}>
          <NRRelativeTime variant="caption" color="text.secondary" short={true} value={createdAtAsDate} />
          <CircleIcon
            className="notification-dot"
            sx={{
              fontSize: 8
            }}
          />
        </Box>
      </Box>
    </StyledLink>
  );
}

function NRNotificationsInAppContent({
  notifications = [],
  isLoading = false,
  onNotificationClick,
  onMarkAllAsRead,
  onViewAllNotifications = () => {}
}) {
  const theme = useTheme();
  const hasNotifications = notifications && notifications.length > 0;

  const handleNotificationClick = notificationId => {
    // Call the parent callback to mark as read
    if (onNotificationClick) {
      onNotificationClick(notificationId);
    }
  };

  const handleMarkAllRead = e => {
    e.preventDefault();
    if (onMarkAllAsRead) {
      onMarkAllAsRead();
    }
  };

  return (
    <Paper
      sx={{
        width: 440,
        maxHeight: 792,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <CardHeader
        sx={{
          '& .MuiCardHeader-action': {
            alignSelf: 'center',
            marginTop: 'unset',
            marginRight: 'unset',
            marginBottom: 'unset'
          }
        }}
        title={<Typography variant="h3">Notifications</Typography>}
        action={
          <Typography
            variant="body2"
            color={theme.palette.text.secondary}
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleMarkAllRead}
          >
            Mark all read
          </Typography>
        }
      />
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: hasNotifications ? 'flex-start' : 'center',
          alignItems: hasNotifications ? 'stretch' : 'center',
          flexGrow: 1,
          p: 0,
          overflow: 'auto',
          '&:last-child': {
            pb: 0
          }
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : hasNotifications ? (
          <Box>
            {notifications.map(notification => (
              <NotificationItem key={notification.id} item={notification} onNotificationClick={handleNotificationClick} />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <NoNewNotificationsComponent />
            <Typography pt={2} variant="label">
              No new notifications
            </Typography>
          </Box>
        )}
      </CardContent>
      <CardActions
        sx={{
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default
        }}
      >
        <Button
          variant="text"
          color="secondary"
          endIcon={<ArrowForwardIcon />}
          component={Link}
          to="/notifications-center"
          onClick={onViewAllNotifications}
        >
          View All Notifications
        </Button>
      </CardActions>
    </Paper>
  );
}

export default NRNotificationsInAppContent;
